import type { EligibilityClass, JucoYear } from '../utils/roster-class';
import type { PlayerLevel } from '../constants/playerLevels';
export type BatchExtractionStatus =
  | 'uploading'
  | 'splitting'
  | 'extracting'
  | 'review'
  | 'distributing'
  | 'failed';

export type PageStatus =
  | 'pending'
  | 'assessing'
  | 'extracting'
  | 'completed'
  | 'failed';

export type UploadType = 'single_team_roster' | 'showcase';

export type BatchExtractionSummary = {
  id: string;
  name: string;
  uploadType: UploadType;
  totalPages: number;
  status: BatchExtractionStatus;
  createdAt: string;
  creatorName: string | null;
  totalRecords: number;
  distributionCount: number;
};

export type BatchExtractionListResponse = {
  items: BatchExtractionSummary[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
};

export type BatchExtractionPage = {
  id: string;
  pageNumber: number;
  pageImageUrl: string | null;
  extractionId: string | null;
  status: PageStatus;
  errorMessage: string | null;
  teamName: string | null;
  recordCount: number;
};

export type BatchExtractionRecord = {
  id: string;
  batchId: string;
  pageId: string | null;
  pageNumber: number | null;
  teamName: string | null;
  teamColor: string | null;
  teamNumber: string | null;
  /**
   * The team (in a target organization) this record has been explicitly assigned
   * to for distribution. `null` means unassigned — the commit gate
   * (`validate-commit` / `distribute`) treats unassigned records as unresolved
   * and blocks distribution. Distinct from `teamName`, which is the raw extracted
   * label; `resolvedTeamId` is the operator's deliberate per-record resolution.
   */
  resolvedTeamId: string | null;
  /**
   * Display name of the team `resolvedTeamId` points at (may belong to another org). Returned by
   * the API so the review table can show the real assigned-team name and re-hydrate its name cache
   * on reload instead of a generic placeholder. `null` when unassigned or the team was deleted.
   */
  resolvedTeamName: string | null;
  jerseyNumber: number | null;
  firstName: string | null;
  lastName: string | null;
  gradYear: number | null;
  /**
   * Year of eligibility ("1".."5") when the roster said so. Optional because an API deployed
   * before this field existed simply omits it - the web and API ship as two workflows, so a
   * new client can be talking to an older server.
   */
  eligibilityClass?: EligibilityClass | null;
  /**
   * Year of junior college ("1".."3") read off the same Class cell. Only meaningful once
   * `playerLevel` says JUCO — a four-year roster carries readings here too.
   *
   * Optional for the same reason as `eligibilityClass`: web and API ship as two workflows, so a
   * new client can be talking to an older server that omits the field entirely.
   */
  jucoYear?: JucoYear | null;
  /**
   * Where the roster says the player sits. Set by the reviewer's per-page JUCO marking, never
   * inferred from a Class cell.
   *
   * `0` is High School and is falsy — test this with `!= null`, never for truthiness.
   */
  playerLevel?: PlayerLevel | null;
  position: string | null;
  bats: string | null;
  throws: string | null;
  height: string | null;
  weight: number | null;
  phone: string | null;
  email: string | null;
  city: string | null;
  state: string | null;
  /**
   * The mailing address as the roster printed it, kept verbatim - a street line on some rosters,
   * a whole one-line address on others. Distributed onto `prospect_about.address`, where the
   * newer roster's value replaces the stored one.
   *
   * Optional because an API deployed before this field existed omits it: the web and API ship as
   * two workflows, so a new client can be talking to an older server.
   */
  address?: string | null;
  twitter: string | null;
  instagram: string | null;
  school: string | null;
  extraFields: Record<string, string> | null;
  isManualEntry: boolean;
};

export type BatchDistribution = {
  id: string;
  organizationId: string;
  organizationName: string | null;
  status: 'pending' | 'in_progress' | 'completed' | 'failed';
  prospectsCreated: number;
  teamsCreated: number;
  errorMessage: string | null;
  distributedAt: string | null;
  createdAt: string;
};

export type BatchExtractionStats = {
  totalRecords: number;
  completedPages: number;
  failedPages: number;
  pendingPages: number;
  uniqueTeams: number;
  teamNames: string[];
};

export type BatchExtraction = {
  id: string;
  name: string;
  uploadType: UploadType;
  sourcePdfUrl: string | null;
  totalPages: number;
  status: BatchExtractionStatus;
  defaultTeamName: string | null;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  creatorName: string | null;
  pages: BatchExtractionPage[];
  records: BatchExtractionRecord[];
  distributions: BatchDistribution[];
  stats: BatchExtractionStats;
};

export type BatchExtractionStatusResponse = {
  id: string;
  status: BatchExtractionStatus;
  totalPages: number;
  completedPages: number;
  failedPages: number;
  processingPages: number;
  pages: PageStatusResponse[];
};

export type PageStatusResponse = {
  id: string;
  pageNumber: number;
  status: PageStatus;
  errorMessage: string | null;
  recordCount: number;
};

export type UploadUrlResponse = {
  batchId: string;
  uploadUrl: string;
  objectName: string;
  expiresAt: string;
};

export type CreateBatchRequest = {
  name: string;
  uploadType: UploadType;
};

export type UpdateRecordsRequest = {
  records: BatchExtractionRecordUpdate[];
};

export type BatchExtractionRecordUpdate = {
  id: string;
  /**
   * Per-record team assignment for distribution. Tri-state semantics match the
   * API contract:
   * - omitted / `undefined` → leave the existing assignment unchanged
   * - a team Guid string → assign to that team
   * - `CLEAR_RESOLVED_TEAM` (the all-zeros Guid) → clear the assignment
   */
  resolvedTeamId?: string;
  teamName?: string;
  teamColor?: string;
  teamNumber?: string;
  jerseyNumber?: number;
  firstName?: string;
  lastName?: string;
  gradYear?: number;
  /** Year of eligibility ("1".."5"). Omitted leaves it unchanged. */
  eligibilityClass?: EligibilityClass;
  /** Year of junior college ("1".."3"). Omitted leaves it unchanged. */
  jucoYear?: JucoYear;
  /** Blanks the JC year. Needed because omitting a field already means "leave alone". */
  clearJucoYear?: boolean;
  position?: string;
  bats?: string;
  throws?: string;
  height?: string;
  weight?: number;
  phone?: string;
  email?: string;
  city?: string;
  state?: string;
  /** The printed mailing address, verbatim. Omitted leaves it unchanged. Max 500 characters. */
  address?: string;
  twitter?: string;
  instagram?: string;
  school?: string;
};

export type CreateManualRecordRequest = {
  teamName?: string;
  teamColor?: string;
  teamNumber?: string;
  jerseyNumber?: number;
  firstName?: string;
  lastName?: string;
  gradYear?: number;
  /** Year of eligibility ("1".."5"). Omitted leaves it unchanged. */
  eligibilityClass?: EligibilityClass;
  /** Year of junior college ("1".."3"), when the operator knows it. */
  jucoYear?: JucoYear;
  /** Classifies the record outright, for a row typed into a JUCO page. */
  playerLevel?: PlayerLevel;
  position?: string;
  bats?: string;
  throws?: string;
  height?: string;
  weight?: number;
  phone?: string;
  email?: string;
  city?: string;
  state?: string;
  /** The player's mailing address. Max 500 characters. */
  address?: string;
  twitter?: string;
  instagram?: string;
  school?: string;
};

export type UpdateTeamsRequest = {
  teams: TeamUpdate[];
};

export type PageTeamNameUpdate = {
  pageId: string;
  teamName: string | null;
};

export type BulkUpdatePageTeamNamesRequest = {
  updates: PageTeamNameUpdate[];
};

export type TeamUpdate = {
  originalName: string;
  newName: string;
};

export type DistributeRequest = {
  organizationIds: string[];
};

export type DistributionResult = {
  batchId: string;
  totalOrganizations: number;
  successfulDistributions: number;
  failedDistributions: number;
  results: DistributionOrgResult[];
};

export type DistributionOrgResult = {
  organizationId: string;
  organizationName: string;
  success: boolean;
  prospectsCreated: number;
  teamsCreated: number;
  errorMessage: string | null;
};

export type PageImageUrlResponse = {
  signedUrl: string;
  expiresAt: string;
};

/**
 * Sentinel Guid the API interprets as "clear this record's team assignment".
 * Send this as `resolvedTeamId` on a record update to unassign it.
 */
export const CLEAR_RESOLVED_TEAM = '00000000-0000-0000-0000-000000000000';

/**
 * Pre-flight check before distributing to a single organization. POSTed to
 * `/validate-commit` to drive the commit gate in the UI [SpecFlow C5].
 */
export type BatchCommitValidationRequest = {
  organizationId: string;
};

/**
 * Result of the pre-flight commit check. A record is "resolved" for an org when
 * its `resolvedTeamId` points to a non-deleted team in that org. When `isValid`
 * is false, `unresolvedRecordIds` lists the records the operator must assign (or
 * remove) before the commit can proceed.
 */
export type BatchCommitValidationResult = {
  isValid: boolean;
  unresolvedRecordIds: string[];
  /**
   * Records marked JUCO that carry no JC year. Kept apart from `unresolvedRecordIds` because the
   * two need different words and different fixes — one wants a team picked, the other a year
   * filled.
   *
   * Optional: an API deployed before this gate existed omits it, and the UI must not read
   * `undefined.length`.
   */
  jucoYearMissingRecordIds?: string[];
  message: string | null;
};

/** Marks a set of records as JUCO, or clears the marking. */
export type SetRecordsJucoRequest = {
  recordIds: string[];
  isJuco: boolean;
  /** Omitted means `page`. */
  scope?: JucoScope;
};

/** Fills one JC year across many records at once. */
export type FillRecordsJucoYearRequest = {
  recordIds: string[];
  jucoYear: JucoYear;
  /** Only touch records with no JC year, so a bulk fill answers the blanks. */
  onlyBlank: boolean;
  /** Omitted means `page`. */
  scope?: JucoScope;
};

/**
 * Which records a JUCO marking or JC-year fill acts on.
 *
 * `page` acts on the `recordIds` sent. `batch` ignores them: the server selects every record in
 * the batch, across all pages and team names, and refuses (409) while any page is still extracting.
 */
export type JucoScope = 'page' | 'batch';

/**
 * The server's reply to a JUCO marking or fill. Counts are records actually changed.
 *
 * `scope` is absent from an API that predates the batch scope, which would have ignored a batch
 * request and changed nothing.
 */
export type JucoActionResponse = {
  message: string;
  updated?: number;
  filled?: number;
  scope?: JucoScope;
};

/**
 * Shape of the `400 Bad Request` body returned by `/distribute` when the commit
 * gate rejects the request because a target org has unresolved records. Nothing
 * is committed in this case. Surfaced via `ApiError.response`.
 */
export type DistributeRejection = {
  message: string;
  organizationId: string;
  unresolvedRecordIds: string[];
  /** Records marked JUCO with no JC year. Absent on an API deployed before this gate existed. */
  jucoYearMissingRecordIds?: string[];
};
